<script setup lang="ts">
import { inject } from "vue";
import Props from "@/ui/components/switch/switch-types";

const check = defineModel<boolean>();
const loading = defineModel<boolean>("loading");
const props = inject<Props>("switch-props");
</script>

<template>
	<div class="switch d-flex align-center ga-2">
		<input
			:disabled="props.disabled"
			type="checkbox"
			v-model="check"
			:inert="loading"
			:aria-checked="check ? 'true' : 'false'"
		/>
		<span class="loading" v-if="loading">
			<VProgressCircular
				color="blue-accent"
				indeterminate
				size="12"
				width="2"
			/>
		</span>
	</div>
</template>

<style scoped lang="scss">
.switch {
	position: relative;
	--br: 10px;
	--size: 1.25em;
	--bg: rgb(var(--v-theme-surface-variant));
	--fg: rgba(255, 255, 255, 0.906);
	--bg-active: rgb(var(--v-theme-primary));
	--fg-active: var(--fg);

	@mixin handle-element {
		position: absolute;
		width: calc(var(--size) - 8px);
		height: calc(var(--size) - 8px);
		aspect-ratio: 1;
		border-radius: 50%;
		transition: all 250ms ease-in-out;
	}

	input {
		position: relative;
		height: var(--size);
		width: 34px;
		border-radius: var(--br);
		appearance: none;
		outline-offset: 2px;
		border: transparent;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		&::before,
		&::after {
			content: "";
			display: block;
			top: 4px;
			left: 4px;
			@include handle-element;
		}

		&::before {
			top: 0;
			padding: 2px;
			width: 100%;
			height: 100%;
			left: 0;
			opacity: 0.6;
			border-radius: var(--br);
			background: var(--bg);
			box-sizing: border-box;
			box-shadow: var(--box-shadow);
			transition: background 250ms ease-in-out;
		}

		&::after {
			background: var(--fg);
		}

		&:disabled {
			cursor: not-allowed;
			background: var(--shade-02);
		}

		&[aria-checked="true"] {
			&::before {
				opacity: 1;
				background: var(--bg-active);
			}

			&::after {
				left: calc(100% - var(--size) + 4px);
			}
		}
	}

	.loading {
		top: -3px;
		left: 4px;
		transition: all 250ms ease-in-out;
		@include handle-element;
	}

	&:has(input[aria-checked="true"]) .loading {
		left: calc(100% - var(--size) + 4px);
	}
}
</style>
